import AppContext from 'App/AppContext';
import { IPIconButton, IPModal, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ModifyTextDialog from 'components/Dialogs/ModifyTextDialog';
import ButtonDialogFooter from 'components/Footer/ButtonDialogFooter';
import SharepointListItemPicker, { ParentListInformation } from 'components/SharePoint/SharepointListItemPicker';
import { cancelIcon, globalTextStylesCardRed } from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLinkNameFromListItem, IListItem } from 'services/Graph/SharepointInterfaces';

interface ILinkCreatorCustomListProps {
  isOpen: boolean;
  list: ResourceList;
  dismiss: () => void;
  addLinks: (links: ResourceLink[]) => void;
  listLinks?: ResourceLink[];
  existingLinks: ResourceLink[];
  listLoading: boolean;
  linkToEdit?: ResourceLink;
  hideWholeList?: boolean;
  singleSelect?: boolean;
}

const LinkCreatorCustomList = (props: ILinkCreatorCustomListProps) => {
  const { t } = useTranslation(['library', 'translation']);
  const appContext = useContext(AppContext);
  const [selectedListItems, setSelectedListItems] = useState<IListItem[]>([]);
  const [linkPickError, setLinkPickError] = useState<boolean>(false);
  const [askUserForLinkName, setAskUserForLinkName] = useState<ResourceLink | undefined>(undefined);

  const createNewLinkFromListItem = (item: IListItem): ResourceLink => {
    let output: ResourceLink;
    if (props.linkToEdit) {
      output = props.linkToEdit.clone();
    } else {
      output = new ResourceLink();
    }

    output.listId = props.list.listId;
    output.list = props.list;
    output.linkName = getLinkNameFromListItem(item);
    output.linkURL = output.buildLinkURLFromListItem(item) ?? 'Unknown';
    output.listItemId = item.id;

    return output;
  };

  const onSubmitWholeListItem = () => {
    const existingLink = props.listLinks?.find((l) => l.isWholeList());
    let newLink: ResourceLink;
    if (existingLink) {
      newLink = existingLink;
    } else {
      newLink = new ResourceLink();
      newLink.setWholeList(props.list);
    }
    props.addLinks([newLink]);
    props.dismiss();
  };

  const getExistingLink = (links: ResourceLink[], newLink: ResourceLink): ResourceLink | undefined => {
    const linkWithId = links.find((link) => link.listItemId === newLink.listItemId);
    const linkWithName = links.find((link) => link.linkName.toLowerCase() === newLink.linkName.toLowerCase());

    if (linkWithId) {
      return linkWithId;
    } else if (linkWithName) {
      const linkCopies = links.filter((link) =>
        link.linkName.toLowerCase().startsWith(newLink.linkName.toLowerCase() + ' ('),
      );
      newLink.linkName += ` (${linkCopies.length + 2})`;

      return newLink;
    }

    return undefined;
  };

  const onSubmit = async () => {
    if (disableAdd() || selectedListItems.length === 0) return;

    try {
      appContext.showContentLoader();
      const newLinks: ResourceLink[] = [];

      for (let idx = 0; idx < selectedListItems.length; idx++) {
        const selectedListItem = selectedListItems[idx];
        const newLink = createNewLinkFromListItem(selectedListItem);
        if (props.linkToEdit) {
          //always push the item from the picker, this is already based on the linkToEdit
          newLinks.push(newLink);
        } else {
          const existingLink = getExistingLink(props.listLinks ?? [], newLink);
          if (existingLink) {
            //user choose a link that was already in the library
            newLinks.push(existingLink);
          } else {
            //user choose a new link from sharepoint
            if (!newLink.linkName) {
              //could not determine the name for the new item. Ask the user
              setAskUserForLinkName(newLink);

              return;
            } else {
              newLinks.push(newLink);
            }
          }
        }
      }

      props.addLinks(newLinks);
      props.dismiss();
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onPickListItem = (items: IListItem[]) => {
    setLinkPickError(false);

    if (items.length) {
      setSelectedListItems(items);
    } else {
      setSelectedListItems([]);
    }
  };

  const disableAdd = () => {
    if (selectedListItems.length === 0) return true;

    if (linkPickError) {
      return true;
    }

    return false;
  };

  const onCancel = () => {
    props.dismiss();
  };

  const getParentInfo = (): ParentListInformation | undefined => {
    if (props.list.spListId && props.list.spSiteId) {
      const parentFolderInfo: ParentListInformation = {
        listId: props.list.spListId,
        siteId: props.list.spSiteId,
        tenantId: props.list.altTenantId,
      };

      return parentFolderInfo;
    }

    return undefined;
  };

  const checkNewLinkName = (value: string): string | undefined => {
    if (!value) return;
    if (props.listLinks?.find((l) => l.linkName.toLowerCase() === value.toLowerCase())) {
      return t('library:LinkComponents.LinkPickError');
    }
  };

  //
  // Main render
  //
  return (
    <IPModal isBlocking={true} isOpen={props.isOpen} onDismiss={onCancel}>
      <IPStack
        verticalFill
        styles={{ root: { height: '90vh', width: 1000, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
      >
        {/*Header*/}
        <IPStack.Item>
          <IPStack horizontal horizontalAlign={'space-between'}>
            <IPStack.Item>
              {!props.linkToEdit && (
                <IPText variant="xLarge">{t('library:LinkComponents.AddItemOf', { name: props.list.name })}</IPText>
              )}
              {props.linkToEdit && (
                <IPText variant="xLarge">
                  {t('library:LinkComponents.ReplaceItem', { name: props.linkToEdit.linkName, list: props.list.name })}
                </IPText>
              )}
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        {/*Body */}
        <IPStack.Item grow>
          <SharepointListItemPicker
            invalidAuthToken={onCancel}
            onSelect={onPickListItem}
            parentList={getParentInfo()}
            existingLinks={props.existingLinks}
            linkToEdit={props.linkToEdit}
            singleSelect={props.singleSelect}
          />
        </IPStack.Item>
        {linkPickError && (
          <IPStack.Item>
            <IPText styles={globalTextStylesCardRed}>{t('library:LinkComponents.LinkPickError')}</IPText>
          </IPStack.Item>
        )}
        <ModifyTextDialog
          isOpen={askUserForLinkName !== undefined}
          value={askUserForLinkName?.linkName}
          required={true}
          title={t('library:LinkComponents.Dialogs.AskUserForLinkName.Title')}
          label={t('library:LinkComponents.Dialogs.AskUserForLinkName.SubText')}
          maxLength={512}
          onClose={() => {
            setAskUserForLinkName(undefined);
          }}
          onSave={() => {
            if (!askUserForLinkName) return;
            props.addLinks([askUserForLinkName]);
            setAskUserForLinkName(undefined);
            props.dismiss();
          }}
          onUpdate={(value: string) => {
            if (!askUserForLinkName) return;
            const newLink = askUserForLinkName.clone();
            newLink.linkName = value;
            setAskUserForLinkName(newLink);
          }}
          onGetErrorMessage={checkNewLinkName}
        />
        {/*Footer*/}
        <IPStack.Item>
          <ButtonDialogFooter
            primaryText={props.linkToEdit ? t('translation:General.Button.Save') : t('translation:General.Button.Add')}
            secondaryText={t('translation:General.Button.Cancel')}
            onSubmit={() => {
              onSubmit();
            }}
            onCancel={onCancel}
            buttonPosition="end"
            disablePrimary={disableAdd}
            tertiaryText={props.hideWholeList ? undefined : t('library:LinkComponents.WholeList')}
            tertiaryTooltip={
              props.existingLinks.find((l) => l.isWholeList()) !== undefined
                ? t('library:LinkComponents.Validate.ItemExists')
                : undefined
            }
            disableTertiary={() => props.existingLinks.find((l) => l.isWholeList()) !== undefined}
            onTertiary={onSubmitWholeListItem}
          />
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

export default LinkCreatorCustomList;
